import {getTestProps} from '@benchling/js-test-utils';
import {Badge, Box, Stack, Text, Tooltip, createStyles, rem} from '@mantine/core';
import type {MantineSize} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React from 'react';

import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Canonical union for the shared input chip. Structurally identical to
 * `ComponentKind` (new-prediction/newPredictionState) and `PredictionInputType`
 * (prediction-jobs/predictionInputs), so both call sites pass their own type
 * with no cast and neither module needs to change.
 */
export type InputChipType = 'aminoAcids' | 'smiles';

const TYPE_ICON: Record<InputChipType, BenchlingIconName> = {
  aminoAcids: 'protein',
  smiles: 'molecule',
};

// Tooltip-only fallback when an input has no name (listing, icon-only chips).
const TYPE_FALLBACK_LABEL: Record<InputChipType, string> = {
  aminoAcids: 'AA Sequence',
  smiles: 'Molecule',
};

interface InputChipProps {
  type: InputChipType;
  /**
   * Set when the input is a registry entity: the chip shows the entity's name instead of `label`, and
   * a tooltip on hover or keyboard focus places it (label, type, size).
   */
  entityName?: string;
  /**
   * Whether an entity chip takes keyboard focus to show its details. Turn off when the chip sits inside
   * another control, such as a button, which must not contain focusable elements.
   */
  focusable?: boolean;
  /** Visible chip text. Omit to render an icon-only chip (listing, unnamed inputs). */
  label?: string;
  /** Defaults to `sm` for compact listings; use `md` in editors like component cards. */
  size?: Extract<MantineSize, 'sm' | 'md'>;
  /** Show the label/type fallback tooltip when value is empty. Non-empty values always show a tooltip. */
  tooltip?: boolean;
  /** Full sequence/SMILES; shown untruncated in the tooltip when non-empty. */
  value?: string;
}

/**
 * The shared chip representing a prediction input's type (and, when known, its name/label):
 * neutral grey for typed values, blue for registry entities. Used on both the New Job inputs table and the Predictions listing so
 * the two stay visually identical.
 */
export default function InputChip({
  type,
  entityName,
  focusable = true,
  label,
  size = 'sm',
  tooltip = false,
  value,
}: InputChipProps): JSX.Element {
  const {classes} = useStyles({size});
  const entityDescriptionId = useId();
  const badgeSize = size === 'md' ? 'lg' : 'sm';
  const hasValue = value != null && value.length > 0;
  const isEntity = entityName != null;
  const entityDetails = [label, TYPE_FALLBACK_LABEL[type]];
  if (type === 'aminoAcids' && hasValue) {
    entityDetails.push(`${value.length} aa`);
  }
  const entityDetailsText = entityDetails.filter((detail) => detail != null).join(' · ');
  const entitySmiles = type === 'smiles' && hasValue ? value : null;
  const badge = (
    <Badge
      // An entity chip's details only show in its tooltip, so keyboard users need to reach it, and
      // screen readers get the same details as a description.
      aria-describedby={isEntity ? entityDescriptionId : undefined}
      classNames={{inner: classes.inner, leftSection: classes.leftSection, root: classes.root}}
      color={isEntity ? 'blue' : 'gray'}
      leftSection={<BenchlingIcon aria-hidden={true} className={classes.icon} icon={TYPE_ICON[type]} />}
      radius="sm"
      size={badgeSize}
      tabIndex={isEntity && focusable ? 0 : undefined}
      tt="none"
      variant="light"
      {...getTestProps({component: 'InputChip', element: 'root'})}
    >
      {entityName ?? label ?? ''}
    </Badge>
  );

  if (isEntity) {
    return (
      <Box className={classes.entityRoot}>
        <Tooltip
          classNames={{tooltip: classes.tooltip}}
          events={{hover: true, focus: true, touch: false}}
          label={
            <Stack spacing={2}>
              <Text fw={600} size="sm">
                {entityName}
              </Text>
              <Text size="xs">{entityDetailsText}</Text>
              {entitySmiles != null && (
                <Text className={classes.smiles} size="xs">
                  {entitySmiles}
                </Text>
              )}
            </Stack>
          }
          multiline={true}
          openDelay={200}
          position="top-start"
          withinPortal={true}
          zIndex={ZINDEX_FLOATING_UI}
        >
          {badge}
        </Tooltip>
        <span className={classes.visuallyHidden} id={entityDescriptionId}>
          {entitySmiles == null ? entityDetailsText : `${entityDetailsText}, ${entitySmiles}`}
        </span>
      </Box>
    );
  }

  if (!hasValue && !tooltip) {
    return badge;
  }

  return (
    <Tooltip
      classNames={{tooltip: classes.tooltip}}
      label={hasValue ? value : (label ?? TYPE_FALLBACK_LABEL[type])}
      multiline={true}
      withinPortal={true}
      zIndex={ZINDEX_FLOATING_UI}
    >
      {badge}
    </Tooltip>
  );
}

const CHIP_MIN_HEIGHTS: Record<Extract<MantineSize, 'sm' | 'md'>, number> = {
  sm: 18,
  // Nucleus `md` badges are only 2px taller than `sm`, so editor chips map to `lg`.
  md: 24,
};

const useStyles = createStyles((theme, {size}: {size: Extract<MantineSize, 'sm' | 'md'>}) => ({
  // Anchors the visually hidden description. Unanchored, its containing block is the nearest positioned
  // ancestor, which can sit outside the scroll region the chip renders in: it then escapes that region's
  // clipping and adds a second scrollbar.
  entityRoot: {
    display: 'inline-flex',
    position: 'relative',
  },
  // The Nucleus Badge theme fixes root height per size and clips `inner`, which crops descenders
  // (e.g. the "g" in "Ligand"). Let the chip grow to fit its text, matching StatusBadge.
  inner: {
    overflow: 'visible',
  },
  leftSection: {
    marginRight: size === 'md' ? 6 : undefined,
  },
  icon: {
    fontSize: size === 'md' ? 14 : 12,
  },
  root: {
    height: 'auto',
    minHeight: CHIP_MIN_HEIGHTS[size],
    ...(size === 'md' && {
      paddingLeft: theme.spacing.xs,
      paddingRight: theme.spacing.sm,
    }),
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
  smiles: {
    fontFamily: theme.fontFamilyMonospace,
  },
  tooltip: {
    maxWidth: rem(420),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
}));
